import { useState } from 'react';
import { Ruler } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface BulkDimsPopoverProps {
  selectedCount: number;
  disabled?: boolean;
  onApply: (dims: { height: number; width: number; length: number; weight: number }) => Promise<void>;
  onApplyAndApprove?: (dims: { height: number; width: number; length: number; weight: number }) => Promise<void>;
}

export default function BulkDimsPopover({ selectedCount, disabled, onApply, onApplyAndApprove }: BulkDimsPopoverProps) {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);

  const allFilled = height !== '' && width !== '' && length !== '' && weight !== '';

  const dims = () => ({
    height: Number(height),
    width: Number(width),
    length: Number(length),
    weight: Number(weight),
  });

  function reset() {
    setOpen(false);
    setHeight(''); setWidth(''); setLength(''); setWeight('');
  }

  async function handleApply() {
    if (!allFilled) return;
    setSaving(true);
    try {
      await onApply(dims());
      reset();
    } finally {
      setSaving(false);
    }
  }

  async function handleApplyAndApprove() {
    if (!allFilled || !onApplyAndApprove) return;
    setSaving(true);
    try {
      await onApplyAndApprove(dims());
      reset();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Ruler className="h-3.5 w-3.5 text-blue-600" />
          Set dims
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start">
        <div className="p-3">
          <div className="text-sm font-medium mb-3">
            Set dims for {selectedCount} item{selectedCount !== 1 ? 's' : ''}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-muted-foreground">
              Height (in)
              <input
                type="number" min="0" step="any" value={height}
                onChange={e => setHeight(e.target.value)}
                className="mt-0.5 w-full rounded border border-zinc-200 px-2 py-1 font-mono text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
            </label>
            <label className="text-[10px] text-muted-foreground">
              Width (in)
              <input
                type="number" min="0" step="any" value={width}
                onChange={e => setWidth(e.target.value)}
                className="mt-0.5 w-full rounded border border-zinc-200 px-2 py-1 font-mono text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
            </label>
            <label className="text-[10px] text-muted-foreground">
              Length (in)
              <input
                type="number" min="0" step="any" value={length}
                onChange={e => setLength(e.target.value)}
                className="mt-0.5 w-full rounded border border-zinc-200 px-2 py-1 font-mono text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
            </label>
            <label className="text-[10px] text-muted-foreground">
              Weight (lb)
              <input
                type="number" min="0" step="any" value={weight}
                onChange={e => setWeight(e.target.value)}
                className="mt-0.5 w-full rounded border border-zinc-200 px-2 py-1 font-mono text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
            </label>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" disabled={!allFilled || saving} onClick={() => void handleApply()}>
              {saving ? 'Applying…' : 'Apply'}
            </Button>
            {onApplyAndApprove && (
              <Button size="sm" variant="outline" disabled={!allFilled || saving} onClick={() => void handleApplyAndApprove()}
                className="border-green-300 text-green-700 hover:bg-green-50">
                {saving ? 'Applying…' : 'Apply & Approve'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Source will change to <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] text-purple-800">Manual</span>
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
